/**
 * 「行为对比」发起按钮：放在对比页筛选栏「基于当前查询发起任务」旁边。
 *
 * 点击后在确认弹窗里选对比范围：全部共同场景（默认）或仅结论翻转的场景。
 * 平台先算两侧评估结论翻转的场景，算完即可在下方区块查看，其余场景在后台继续算，按钮上显示进度。
 *
 * 状态：没发起过 → 「发起行为对比」；计算中 → 「行为对比计算中 x/n」（点击无操作，悬停看详情）；
 * 已完成 → 「重新对比」；失败 → 「重试行为对比」。
 * 预估：没发起过时抽样探测少量场景的指纹是否已就绪，悬停提示预计快慢（抽样口径，不是精确值）。
 */
import { ExperimentOutlined, InfoCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { Button, Input, Modal, Radio, Segmented, Select, Tooltip, message } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';

import DailyBaseVersionPicker from '@components/CreateJobModal/DailyBaseVersionPicker';
import { renderProbeHint, renderProbeSuffix } from '@components/CreateJobModal/probeIndicators';
import { usePackageExistsProbe } from '@components/CreateJobModal/usePackageExistsProbe';

import {
  getBehaviorDefaultPackage,
  getBehaviorProgress,
  getDailyTagsExisting,
  getDailyVersionsByTag,
  type DailyVersionItem,
  type BehaviorDefaultPackage,
  getBehaviorReadiness,
  launchBehaviorCompare,
  type BehaviorCompareScope,
  type BehaviorProgress,
} from '@/apis/simulation';

import { estimateMinutes } from './format';

interface Props {
  jobId: number;
  compareJobId: number;
  /** 进度变化（含完成）时通知父级刷新区块 / 明细表的行为数据 */
  onProgress?: (p: BehaviorProgress) => void;
}

const POLL_MS = 5000;
const SAMPLE = 40;
const IN_PROGRESS = ['running', 'flipped_ready'];

/**
 * 范围选择 + 一行随选择变化的说明。
 *
 * 弹窗是命令式调的（Modal.confirm），内容区不会随外部 state 重渲染，
 * 所以说明文字只能靠这里的内部 state 切；选中结果同步写回 ``choice``。
 */
function ScopeChooser({
  minutes, flippedMinutes, probing, flipped, choice,
}: {
  minutes: number | null; flippedMinutes: number | null; probing: boolean; flipped: number | null;
  choice: { scope: BehaviorCompareScope };
}) {
  const [scope, setScope] = useState<BehaviorCompareScope>('all');
  // 预估要现场探测指纹（约十几秒），探测期间给个占位，免得那行先是空的、过一会儿才冒出来
  const allHint = minutes ? `预计约 ${minutes} 分钟。` : (probing ? '正在估算耗时…' : '');
  const flippedHint = flipped === null
    ? (probing ? '正在统计翻转场景…' : '')
    : flipped > 0
      ? `仅算翻转场景 ${flipped} 个`
        + (flippedMinutes ? `，预计约 ${flippedMinutes} 分钟。` : '。')
      : '无翻转场景，无法发起。';
  const hint = scope === 'all' ? allHint : flippedHint;
  return (
    <div>
      <Radio.Group
        value={scope}
        onChange={(e) => {
          const next = e.target.value as BehaviorCompareScope;
          choice.scope = next;
          setScope(next);
        }}
      >
        <Radio value="all">全部共同场景</Radio>
        <Radio value="flipped" disabled={flipped === 0}>仅结论翻转的场景</Radio>
      </Radio.Group>
      {hint ? (
        <div
          style={{
            marginTop: 8, padding: '5px 10px', borderRadius: 6,
            background: '#fffbe6', border: '1px solid #ffe58f',
            color: '#ad6800', fontSize: 13, lineHeight: '20px',
          }}
        >
          <InfoCircleOutlined style={{ marginRight: 6 }} />
          {hint}
        </div>
      ) : null}
    </div>
  );
}

type PkgMode = 'auto' | 'version' | 's3' | 'daily';

const PKG_MODE_LABELS: { label: string; value: PkgMode }[] = [
  { label: '默认产物', value: 'auto' },
  { label: '版本号', value: 'version' },
  { label: 'S3 地址', value: 's3' },
  { label: 'daily 版本', value: 'daily' },
];

/**
 * 工具包选择：与「创建任务 → 使用编译好的版本」同一套口径。
 *
 * - 最新产物：预填后端自动解析的结果（当天最新 orin_dev 编译产物，取不到则配置兜底）
 * - 版本号 / daily 版本：填版本号，后端按与建任务相同的模板拼包地址
 * - S3 地址：直接填 bos:// 全路径
 * 三处输入都会实时探测对象存储，存在显示绿勾，不存在显示红叉。
 */
function PackageChooser({
  defaultPkg, choice,
}: {
  defaultPkg: BehaviorDefaultPackage | null;
  choice: { packageUrl?: string; packageVersion?: string };
}) {
  const [mode, setMode] = useState<PkgMode>('auto');
  const [version, setVersion] = useState('');
  const [s3, setS3] = useState('');
  const [tag, setTag] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [candidates, setCandidates] = useState<DailyVersionItem[]>([]);

  useEffect(() => {
    if (mode !== 'daily' || tags.length > 0) return;
    let alive = true;
    getDailyTagsExisting()
      .then((list) => alive && setTags(list || []))
      .catch(() => undefined);
    return () => { alive = false; };
  }, [mode, tags.length]);

  const versionProbe = usePackageExistsProbe(version, 'package_neodata', mode === 'version' || mode === 'daily');
  const s3Probe = usePackageExistsProbe(s3, 'package_neodata', mode === 's3');
  const autoProbe = usePackageExistsProbe(
    defaultPkg?.packageUrl || '', 'package_neodata', mode === 'auto' && !!defaultPkg?.packageUrl,
  );

  // 把当前选择同步给命令式弹窗的提交回调
  useEffect(() => {
    if (mode === 'auto') {
      choice.packageUrl = undefined; choice.packageVersion = undefined;
    } else if (mode === 's3') {
      choice.packageUrl = s3.trim() || undefined; choice.packageVersion = undefined;
    } else {
      choice.packageVersion = version.trim() || undefined; choice.packageUrl = undefined;
    }
  }, [mode, version, s3, choice]);

  const pickTag = (next: string) => {
    setTag(next);
    if (!next) { setVersion(''); setCandidates([]); return; }
    getDailyVersionsByTag(next, 20)
      .then((list) => {
        setCandidates(list || []);
        setVersion(list?.[0]?.versionNo || '');
      })
      .catch(() => setCandidates([]));
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ marginBottom: 6 }}>工具包</div>
      <Segmented
        size="small"
        value={mode}
        onChange={(v) => setMode(v as PkgMode)}
        options={PKG_MODE_LABELS}
        style={{ marginBottom: 8 }}
      />
      {mode === 'auto' && (
        <>
          <Input
            readOnly
            value={defaultPkg?.packageUrl || ''}
            placeholder={defaultPkg ? '' : '自动解析失败，请切换到版本号或 S3 地址'}
            suffix={renderProbeSuffix(autoProbe)}
          />
          <div style={{ marginTop: 4, fontSize: 12, color: '#8c8c8c' }}>
            {defaultPkg?.packageSource === 'config' ? '来源：配置兜底' : '来源：自动解析当天最新的 orin_dev 编译产物'}
            {renderProbeHint(autoProbe) ? <><br />{renderProbeHint(autoProbe)}</> : null}
          </div>
        </>
      )}
      {mode === 'version' && (
        <>
          <Input
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            placeholder="请输入版本号（如 20261102.simulation.test.0.01）"
            suffix={renderProbeSuffix(versionProbe)}
            allowClear
          />
          {renderProbeHint(versionProbe) ? <div style={{ marginTop: 4, fontSize: 12 }}>{renderProbeHint(versionProbe)}</div> : null}
        </>
      )}
      {mode === 's3' && (
        <>
          <Input
            value={s3}
            onChange={(e) => setS3(e.target.value)}
            placeholder="bos://brood-package/x86_64/x86_64_<版本>-Orin6.0.9.0.tgz"
            suffix={renderProbeSuffix(s3Probe)}
            allowClear
          />
          <div style={{ marginTop: 4, fontSize: 12, color: '#8c8c8c' }}>
            仅支持 bos:// 开头的完整地址
            {renderProbeHint(s3Probe) ? <><br />{renderProbeHint(s3Probe)}</> : null}
          </div>
        </>
      )}
      {mode === 'daily' && (
        <>
          <Select
            style={{ width: '100%', marginBottom: 8 }}
            value={tag || undefined}
            onChange={pickTag}
            placeholder="选择 daily tag（如 orin_dev / release.10.12）"
            options={tags.map((t) => ({ label: t, value: t }))}
            showSearch
            allowClear
          />
          <DailyBaseVersionPicker
            value={version}
            onChange={setVersion}
            candidates={candidates}
            tagSelected={!!tag}
            placeholder="选 Tag 后自动填入最新；点右侧查看历史或直接输入"
            suffix={renderProbeSuffix(versionProbe)}
          />
          {renderProbeHint(versionProbe) ? <div style={{ marginTop: 4, fontSize: 12 }}>{renderProbeHint(versionProbe)}</div> : null}
        </>
      )}
    </div>
  );
}

export default function LaunchButton({ jobId, compareJobId, onProgress }: Props) {
  const [progress, setProgress] = useState<BehaviorProgress | null>(null);
  const [estimateMin, setEstimateMin] = useState<number | null>(null);
  const [probing, setProbing] = useState(false);
  const [flippedNum, setFlippedNum] = useState<number | null>(null);
  const [flippedMinutes, setFlippedMinutes] = useState<number | null>(null);
  const [defaultPkg, setDefaultPkg] = useState<BehaviorDefaultPackage | null>(null);
  const [launching, setLaunching] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastKey = useRef('');
  // 每次 effect 递增：旧世代在飞的请求回来时直接丢弃，不会把上一对任务的轮询链带活
  const gen = useRef(0);
  // 父级传来的回调每次渲染都是新函数；放进 ref，轮询函数才不会因它变化而重建，
  // 否则「通知父级 → 父级重渲染 → 回调变 → 轮询重启 → 又通知父级」会形成死循环、请求刷屏。
  const onProgressRef = useRef(onProgress);
  onProgressRef.current = onProgress;

  const poll = useCallback(async (myGen: number) => {
    try {
      const p = await getBehaviorProgress(jobId, compareJobId);
      if (gen.current !== myGen) return;
      setProgress(p);
      // 状态或已完成片数变了才通知父级，避免每 5 秒无谓刷新明细表
      const key = `${p.status}:${p.doneShardNum}:${p.failedShardNum}`;
      if (key !== lastKey.current) {
        lastKey.current = key;
        onProgressRef.current?.(p);
      }
      if (IN_PROGRESS.includes(p.status) && gen.current === myGen) {
        timer.current = setTimeout(() => void poll(myGen), POLL_MS);
      }
    } catch {
      if (gen.current === myGen) timer.current = setTimeout(() => void poll(myGen), POLL_MS * 3); // 网络抖动：放慢重试，不弹错
    }
  }, [jobId, compareJobId]);

  useEffect(() => {
    const myGen = ++gen.current;
    lastKey.current = '';
    setProgress(null);
    setEstimateMin(null);
    setFlippedNum(null);
    setFlippedMinutes(null);
    void poll(myGen);
    return () => {
      if (gen.current === myGen) gen.current += 1; // 让本世代的在飞请求作废
      if (timer.current) clearTimeout(timer.current);
    };
  }, [poll]);

  // 默认工具包：弹窗里要预填，不能留空
  useEffect(() => {
    let alive = true;
    getBehaviorDefaultPackage()
      .then((p) => alive && setDefaultPkg(p))
      .catch(() => alive && setDefaultPkg(null));
    return () => { alive = false; };
  }, [jobId, compareJobId]);

  // 没发起过才做预估（抽样探测，几十次存储请求）
  useEffect(() => {
    if (progress?.status !== 'none') return undefined;
    let alive = true;
    setProbing(true);
    getBehaviorReadiness(jobId, compareJobId, true, SAMPLE)
      .then((r) => {
        if (!alive || !r || r.commonSceneNum === 0) return;
        setEstimateMin(estimateMinutes(r.comparableNow, r.sampled || r.commonSceneNum, r.commonSceneNum));
        const flipped = r.flippedSceneNum ?? null;
        setFlippedNum(flipped);
        setFlippedMinutes(
          flipped && flipped > 0
            ? estimateMinutes(r.comparableNow, r.sampled || r.commonSceneNum, flipped)
            : null,
        );
      })
      .catch(() => undefined)
      .finally(() => {
        if (alive) setProbing(false);
      });
    return () => {
      alive = false;
    };
  }, [progress?.status, jobId, compareJobId]);

  const doLaunch = async (scope: BehaviorCompareScope, packageUrl?: string) => {
    if (scope === 'flipped' && !flippedNum) {
      // 弹窗打开时翻转数可能还没探测完；这里兜住竞态，别把注定失败的请求发出去
      message.warning(flippedNum === 0 ? '两侧结论完全相同，无法只对比翻转场景' : '正在统计翻转场景，请稍候再试');
      return;
    }
    setLaunching(true);
    const myGen = gen.current; // 记住发起时属于哪一代任务
    try {
      const r = await launchBehaviorCompare(jobId, compareJobId, scope, packageUrl);
      const range = scope === 'flipped' ? '仅结论翻转的场景' : '全部共同场景';
      const scenes = (r.flippedTotal ?? 0) + (r.otherTotal ?? 0);
      message.success(
        r.reused
          ? '这对任务已有进行中的行为对比，已沿用'
          : `已发起行为对比（${range}）${scenes > 0 ? `，共 ${scenes} 个场景` : ''}`
            + `${r.packageSource ? `；工具包来源：${{ explicit: '手动指定', auto_orin_dev: '自动解析当天最新', config: '配置兜底' }[r.packageSource] ?? r.packageSource}` : ''}`,
      );
      if (gen.current !== myGen) return; // 等待期间切了任务：旧链作废，别去清别人的定时器
      if (timer.current) clearTimeout(timer.current);
      void poll(myGen);
    } catch (e) {
      message.error(e instanceof Error ? e.message : '发起行为对比失败');
    } finally {
      setLaunching(false);
    }
  };

  const onClick = () => {
    const done = progress?.status === 'succeeded';
    // 弹窗内容里的单选结果存这里；Modal.confirm 是命令式调用，内容区自己带状态会用不了
    const choice: { scope: BehaviorCompareScope; packageUrl?: string } = {
      scope: 'all',
      packageUrl: defaultPkg?.packageUrl || undefined,
    };
    Modal.confirm({
      title: done ? '重新发起行为对比？' : '发起行为对比？',
      width: 560,
      content: (
        <div>
          <div style={{ marginBottom: 6 }}>对比范围：</div>
          <ScopeChooser minutes={estimateMin} flippedMinutes={flippedMinutes} probing={probing} flipped={flippedNum} choice={choice} />
          <PackageChooser defaultPkg={defaultPkg} choice={choice} />
          {done ? <div style={{ marginTop: 6 }}>已有结果会被新结果取代。</div> : null}
        </div>
      ),
      okText: '发起',
      cancelText: '取消',
      onOk: () => doLaunch(choice.scope, choice.packageUrl),
    });
  };

  const running = !!progress && IN_PROGRESS.includes(progress.status);
  // 进度用「场景」表达，不用「片」：片是计算侧的调度单位，对使用者没有意义
  const sceneTotal = (progress?.flippedTotal ?? 0) + (progress?.otherTotal ?? 0);
  let label = '发起行为对比';
  if (running) {
    label = sceneTotal > 0
      ? `行为 diff 对比中 ${progress!.computedSceneNum}/${sceneTotal}`
      : `行为 diff 对比中 ${progress!.computedSceneNum}`;
  }
  else if (progress?.status === 'succeeded') label = '重新对比行为 diff';
  else if (progress?.status === 'failed') label = '重试行为 diff 对比';

  const rangeText = progress?.scope === 'flipped' ? '仅结论翻转的场景' : '全部共同场景';
  const tip = running
    ? `已算完 ${progress!.computedSceneNum}${sceneTotal > 0 ? `/${sceneTotal}` : ''} 个场景`
      + `${progress!.flippedReady ? '，结论翻转的场景已全部算完，可先查看' : ''}`
      + `${progress!.failedShardNum ? '；有部分场景没算出结果（计算分片失败），页面不显示它们的数据' : ''}`
      + `（范围：${rangeText}${progress!.packageUrl ? `，工具包 ${progress!.packageUrl.split('/').pop()}` : ''}）`
    : progress?.status === 'failed'
      ? progress.message || '上次对比失败'
      : progress?.status === 'succeeded' && progress.message
        ? progress.message
        : estimateMin ? `对两个任务的${rangeText}做行为对比，预计约 ${estimateMin} 分钟` : `对两个任务的${rangeText}做行为对比`;

  return (
    <Tooltip title={tip}>
      <span style={{ display: 'inline-block' }}>
        <Button
          size="small"
          type="primary"
          icon={running ? <LoadingOutlined /> : <ExperimentOutlined />}
          loading={launching}
          disabled={running || progress == null}
          onClick={onClick}
          style={{ background: running ? '#8b93e0' : '#5e6ad2', borderColor: 'transparent', boxShadow: '0 1px 2px rgba(94,106,210,.28)' }}
        >
          {label}
        </Button>
      </span>
    </Tooltip>
  );
}
